// Corrects "impossible" variant selections before they're sent to the server.
//
// Some products (e.g. Fabric Colour x Trim Colour x Size) don't fully cross their
// options — not every Trim exists for every Fabric. Picking a swatch that has never
// been paired with the currently selected value(s) of another option lands on a
// combination with no matching variant at all. `product.selected_or_first_available_variant`
// then silently falls back to an unrelated variant (typically the product's first),
// so price/SKU/gallery show that fallback's data while the swatches still display the
// combination the shopper actually clicked.
//
// This runs in the capture phase, ahead of <variant-selects>'s own bubble-phase
// listener (assets/global.js), so by the time it reads `selectedOptionValues` the
// DOM already reflects a real, existing variant.
(function () {
  var initialized = new WeakSet();

  function readMatrix(root) {
    var script = root.querySelector('[data-variant-matrix]');
    if (!script) return null;

    try {
      var variants = JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }

    return Array.isArray(variants) && variants.length ? variants : null;
  }

  function init(root) {
    var variants = readMatrix(root);
    if (!variants) return;

    var inputsByPosition = {};
    var positions = [];

    function refreshInputs() {
      inputsByPosition = {};
      root.querySelectorAll('[data-option-position]').forEach(function (el) {
        var position = el.dataset.optionPosition;
        (inputsByPosition[position] = inputsByPosition[position] || []).push(el);
      });
      positions = Object.keys(inputsByPosition);
    }

    refreshInputs();
    if (positions.length < 2) return; // nothing to cross-check against

    function getSelectedValue(position) {
      var select = root.querySelector('select.select__select[data-option-position="' + position + '"]');
      if (select) return select.value || undefined;

      var els = inputsByPosition[position] || [];
      for (var i = 0; i < els.length; i++) {
        if (els[i].tagName === 'INPUT' && els[i].checked) return els[i].value;
      }
      return undefined;
    }

    function syncSelectValue(select, value) {
      if (!select) return;
      select.value = value;
      Array.from(select.options).forEach(function (option) {
        var on = option.value === select.value;
        option.selected = on;
        if (on) {
          option.setAttribute('selected', 'selected');
        } else {
          option.removeAttribute('selected');
        }
      });
    }

    function setSelectedValue(position, value) {
      var select = root.querySelector('select.select__select[data-option-position="' + position + '"]');
      if (select) {
        syncSelectValue(select, value);
        return;
      }

      var els = inputsByPosition[position] || [];
      for (var i = 0; i < els.length; i++) {
        if (els[i].tagName === 'INPUT' && els[i].value === value) {
          els[i].checked = true;
          return;
        }
      }
    }

    function currentSelections() {
      var current = {};
      positions.forEach(function (position) {
        current[position] = getSelectedValue(position);
      });
      return current;
    }

    function validNamesForPosition(position, current) {
      var names = {};
      variants.forEach(function (variant) {
        // Keep a tracked, zero-stock variant that does not continue selling so
        // the dropdown can list it as Sold out. Skip every other unavailable
        // combination — those have no variant for the earlier options.
        if (!variant.available && !variant.soldOut) return;
        var matchesPrevious = positions.every(function (other) {
          if (Number(other) >= Number(position)) return true;
          return !current[other] || variant['o' + other] === current[other];
        });
        if (!matchesPrevious) return;
        var name = variant['o' + position];
        if (name) names[name] = true;
      });
      return names;
    }

    function rebuildDropdowns(exceptSelect) {
      refreshInputs();
      var current = currentSelections();

      root.querySelectorAll('select.select__select[data-option-position]').forEach(function (select) {
        // Replacing <option> nodes on the select that just changed snaps iOS/iPadOS
        // native pickers back to the page-load value.
        if (exceptSelect && select === exceptSelect) return;

        var template = select.parentElement && select.parentElement.querySelector('[data-option-catalog]');
        if (!template || !template.content) return;

        var position = select.dataset.optionPosition;
        var valid = validNamesForPosition(position, current);
        if (!Object.keys(valid).length) return;

        var desired = current[position];
        var next = document.createDocumentFragment();
        var nextValues = [];
        var match = null;

        template.content.querySelectorAll('option').forEach(function (option) {
          if (!valid[option.value]) return;
          var clone = option.cloneNode(true);
          clone.removeAttribute('selected');
          clone.selected = false;
          if (clone.value === desired) match = clone;
          nextValues.push(clone.value);
          next.appendChild(clone);
        });

        if (!next.childNodes.length) return;

        var currentValues = Array.from(select.options).map(function (option) {
          return option.value;
        });
        if (nextValues.join('\0') === currentValues.join('\0')) {
          if (desired && select.value !== desired) syncSelectValue(select, desired);
          return;
        }

        select.replaceChildren(next);
        syncSelectValue(select, (match && match.value) || (select.options[0] && select.options[0].value) || '');
      });

      refreshInputs();
    }

    function findClosestVariant(current, fixedPosition, requireAvailable) {
      var best = null;
      var bestScore = -1;

      variants.forEach(function (variant) {
        if (variant['o' + fixedPosition] !== current[fixedPosition]) return;
        if (requireAvailable && !variant.available) return;

        var score = 0;
        positions.forEach(function (position) {
          if (position === fixedPosition) return;
          if (variant['o' + position] === current[position]) score++;
        });

        if (score > bestScore) {
          bestScore = score;
          best = variant;
        }
      });

      return best;
    }

    rebuildDropdowns();

    var rebuildTimer = null;
    function scheduleRebuildDropdowns(exceptSelect) {
      if (rebuildTimer !== null) clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(function () {
        rebuildTimer = null;
        if (!root.isConnected) return;
        rebuildDropdowns(exceptSelect);
      }, 0);
    }

    root.addEventListener(
      'change',
      function (event) {
        var changedPosition = event.target.dataset && event.target.dataset.optionPosition;
        if (!changedPosition) return;

        refreshInputs();
        var current = currentSelections();
        // Native pickers update select.value before option.selected; trust the event.
        if (event.target.value) current[changedPosition] = event.target.value;

        var exists = variants.some(function (variant) {
          return positions.every(function (position) {
            return variant['o' + position] === current[position];
          });
        });

        if (!exists) {
          var best =
            findClosestVariant(current, changedPosition, true) ||
            findClosestVariant(current, changedPosition, false);
          if (best) {
            positions.forEach(function (position) {
              if (position === changedPosition) return;
              var wanted = best['o' + position];
              if (wanted === current[position]) return;
              setSelectedValue(position, wanted);
            });
          }
        }

        var changedSelect = event.target.tagName === 'SELECT' ? event.target : null;
        scheduleRebuildDropdowns(changedSelect);
      },
      true
    );
  }

  function tryInit(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    init(root);
  }

  document.querySelectorAll('variant-selects').forEach(tryInit);

  // <variant-selects> is replaced on a variant/product swap, so watch for new
  // instances arriving anywhere in the page rather than relying on one-time setup.
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('variant-selects')) tryInit(node);
        if (node.querySelectorAll) node.querySelectorAll('variant-selects').forEach(tryInit);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
